import { create } from 'zustand';

/** Discriminator for `useUiStore.modals` — every singleton modal in the app must have a name here. */
export type ModalType =
  | 'creator'
  | 'deleteAccount'
  | 'deleteCreator'
  | 'logout'
  | 'comingSoon'
  | 'support'
  | 'terms'
  | 'echoDetails'
  | 'noCredits'
  | 'stripeRequired'
  | 'alert'
  | 'welcome';

/** Config passed to `openAlert` to populate the global `AlertModal`. */
export interface AlertModalConfig {
  title: string;
  message: string;
  primaryLabel: string;
  primaryAction?: () => void | Promise<void>;
  secondaryLabel?: string;
  secondaryAction?: () => void | Promise<void>;
}

export type BillingCapability = 'TEXT' | 'VOICE' | 'VIDEO';

export interface BillingRecoveryContext {
  capability?: BillingCapability;
  requiredCredits?: number;
  balance?: number;
}

interface UiState {
  modals: Record<ModalType, boolean>;
  alertConfig: AlertModalConfig | null;
  billingRecovery: BillingRecoveryContext | null;
  openModal: (type: ModalType) => void;
  openBillingRecovery: (context?: BillingRecoveryContext) => void;
  closeModal: (type: ModalType) => void;
  toggleModal: (type: ModalType) => void;
  openAlert: (config: AlertModalConfig) => void;
}

/** Global modal/alert orchestration store — single source of truth for "is X modal open?". */
export const useUiStore = create<UiState>((set) => ({
  modals: {
    creator: false,
    deleteAccount: false,
    deleteCreator: false,
    logout: false,
    comingSoon: false,
    support: false,
    terms: false,
    echoDetails: false,
    noCredits: false,
    stripeRequired: false,
    alert: false,
    welcome: false,
  },
  alertConfig: null,
  billingRecovery: null,
  openModal: (type) => set((state) => ({ modals: { ...state.modals, [type]: true } })),
  openBillingRecovery: (context = {}) =>
    set((state) => ({
      modals: { ...state.modals, noCredits: true },
      billingRecovery: context,
    })),
  closeModal: (type) =>
    set((state) => ({
      modals: { ...state.modals, [type]: false },
      ...(type === 'alert' ? { alertConfig: null } : {}),
      ...(type === 'noCredits' ? { billingRecovery: null } : {}),
    })),
  toggleModal: (type) =>
    set((state) => ({ modals: { ...state.modals, [type]: !state.modals[type] } })),
  openAlert: (config) =>
    set((state) => ({
      modals: { ...state.modals, alert: true },
      alertConfig: config,
    })),
}));
